import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, Loader2,
  ChevronRight, CheckCircle, XCircle, Users,
  Home, Briefcase, Star, MoreVertical, Power, Trash2, Award, Check,
} from 'lucide-react';
import { fetchAccounts, createAccount, setAccountActive, deleteAccount, updateAccount } from '@/services/accounts';
import { fetchDepartments, fetchMunicipalities } from '@/services/geo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PermissionGate } from '@/components/PermissionGate';
import { AccountsTabs } from '@/components/AccountsTabs';
import { Pagination, usePagination } from '@/components/Pagination';
import { cn } from '@/lib/utils';
import type { Account, AccountType, AccountTier, GeoDepartment, GeoMunicipality } from '@asi/shared';

const TYPE_CONFIG: Record<AccountType, { label: string; icon: React.ReactNode; color: string }> = {
  residential: { label: 'Residencial', icon: <Home className="w-4 h-4" />, color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  commercial:  { label: 'Comercio',    icon: <Briefcase className="w-4 h-4" />, color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  enterprise:  { label: 'Empresa',     icon: <Star className="w-4 h-4" />, color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
};

const TIER_CONFIG: Record<AccountTier, { label: string; color: string; bg: string }> = {
  A: { label: 'A', color: 'text-amber-700 dark:text-amber-400',  bg: 'bg-amber-500/15 border-amber-400/40' },
  B: { label: 'B', color: 'text-blue-700 dark:text-blue-400',    bg: 'bg-blue-500/15 border-blue-400/40' },
  C: { label: 'C', color: 'text-slate-600 dark:text-slate-400',  bg: 'bg-slate-500/15 border-slate-400/40' },
};

/** Format account age from created_at to a short Spanish string. */
function formatSeniority(createdAt: string): string {
  const start = new Date(createdAt);
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 1) return 'Nuevo';
  if (months < 12) return `${months} mes${months !== 1 ? 'es' : ''}`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${years} año${years !== 1 ? 's' : ''} ${rem} mes${rem !== 1 ? 'es' : ''}` : `${years} año${years !== 1 ? 's' : ''}`;
}

const EMPTY_FORM = {
  name: '',
  type: 'commercial' as AccountType,
  nit: '',
  phone: '',
  billing_email: '',
  billing_name: '',
  address: '',
  department_id: '',
  municipality_id: '',
  monthly_fee: '',
  notes: '',
};

// Module-level cache: stale-while-revalidate so the page never shows an
// empty spinner when revisiting after a tab switch or navigation.
let _cachedAccounts: Account[] | null = null;

export function AccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>(_cachedAccounts ?? []);
  const [loading, setLoading] = useState(_cachedAccounts === null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AccountType | 'ALL'>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<GeoDepartment[]>([]);
  const [municipalities, setMunicipalities] = useState<GeoMunicipality[]>([]);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'INACTIVE'>('ALL');
  const [tierFilter, setTierFilter] = useState<AccountTier | 'ALL'>('ALL');
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpenFor) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenFor(null);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpenFor]);

  async function handleToggleActive(account: Account) {
    setMenuOpenFor(null);
    const next = !account.is_active;
    const action = next ? 'reactivar' : 'desactivar';
    if (!window.confirm(`¿${action[0].toUpperCase() + action.slice(1)} la cuenta "${account.name}"?`)) return;
    setBusyId(account.id);
    try {
      await setAccountActive(account.id, next);
      _cachedAccounts = null;
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : `Error al ${action}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleSetTier(account: Account, tier: AccountTier | null) {
    setMenuOpenFor(null);
    setBusyId(account.id);
    try {
      await updateAccount(account.id, { client_tier: tier });
      _cachedAccounts = null;
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al cambiar clasificación');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(account: Account) {
    setMenuOpenFor(null);
    const confirm1 = window.confirm(
      `¿Eliminar permanentemente la cuenta "${account.name}" (${account.account_number})?\n\nEsta acción NO se puede deshacer. La cuenta y sus facturas, intentos de pago y comprobantes serán borrados. Sitios y dispositivos quedarán huérfanos a menos que confirmes el borrado forzado.`
    );
    if (!confirm1) return;
    const typed = window.prompt(`Escribe el número de cuenta (${account.account_number}) para confirmar:`);
    if (typed !== account.account_number) {
      if (typed !== null) alert('El número no coincide. Eliminación cancelada.');
      return;
    }
    const forceWipe = window.confirm(
      'BORRADO FORZADO: ¿También eliminar sitios, cámaras y paneles de esta cuenta?\n\nOK = sí (recomendado para cuentas de prueba)\nCancelar = no (preservar dispositivos)'
    );
    setBusyId(account.id);
    try {
      await deleteAccount(account.id, { deleteMembers: forceWipe });
      _cachedAccounts = null;
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar';
      alert(`No se pudo eliminar la cuenta:\n${msg}\n\nSi tiene datos asociados, vuelve a intentar con borrado forzado o desactívala.`);
    } finally {
      setBusyId(null);
    }
  }

  async function load() {
    if (_cachedAccounts === null) setLoading(true);
    try {
      const data = await fetchAccounts();
      _cachedAccounts = data;
      setAccounts(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.department_id) {
      fetchMunicipalities(Number(form.department_id))
        .then(setMunicipalities)
        .catch(() => {});
    } else {
      setMunicipalities([]);
    }
    setForm((f) => ({ ...f, municipality_id: '' }));
  }, [form.department_id]);

  const filtered = accounts.filter((a) => {
    const matchSearch =
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.account_number.toLowerCase().includes(search.toLowerCase()) ||
      (a.city ?? '').toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'ALL' || a.type === typeFilter;
    const matchTier = tierFilter === 'ALL' || a.client_tier === tierFilter;
    const matchActive = statusFilter === 'INACTIVE' ? !a.is_active : (showInactive || a.is_active);
    return matchSearch && matchType && matchTier && matchActive;
  });

  const pager = usePagination(filtered, `${search}|${typeFilter}|${tierFilter}|${statusFilter}|${showInactive}`);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const dept = departments.find((d) => d.id === Number(form.department_id));
      await createAccount({
        name: form.name.trim(),
        type: form.type,
        nit: form.nit || undefined,
        phone: form.phone || undefined,
        billing_email: form.billing_email || undefined,
        billing_name: form.billing_name || undefined,
        address: form.address || undefined,
        department_id: form.department_id ? Number(form.department_id) : undefined,
        municipality_id: form.municipality_id ? Number(form.municipality_id) : undefined,
        city: dept?.name ?? undefined,
        monthly_fee: form.monthly_fee ? parseFloat(form.monthly_fee) : undefined,
        notes: form.notes || undefined,
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear cuenta');
    } finally {
      setSaving(false);
    }
  }

  const activeCount = accounts.filter((a) => a.is_active).length;

  return (
    <div className="flex flex-col h-full">
      <AccountsTabs />
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Cuentas
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeCount} cuentas activas · {accounts.length} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate require="accounts.manage">
            <Button size="sm" onClick={() => setShowModal(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Nueva cuenta
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, número o ciudad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          {(['ALL', 'residential', 'commercial', 'enterprise'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                typeFilter === t
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {t === 'ALL' ? 'Todas' : TYPE_CONFIG[t].label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(['ALL', 'A', 'B', 'C'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTierFilter(t)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                tierFilter === t
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-transparent hover:bg-muted/80'
              )}
            >
              {t === 'ALL' ? 'Tier' : `Tier ${t}`}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer ml-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Mostrar inactivas
        </label>
      </div>

      {/* Stats cards — click a card to filter the table below by that type/status */}
      <div className="grid grid-cols-4 gap-4">
        {(['residential', 'commercial', 'enterprise'] as AccountType[]).map((t) => {
          const count = accounts.filter((a) => a.type === t && a.is_active).length;
          const cfg = TYPE_CONFIG[t];
          const isSelected = typeFilter === t && statusFilter === 'ALL';
          return (
            <Card
              key={t}
              className={cn('p-4 cursor-pointer transition-colors hover:bg-muted/40', isSelected && 'ring-2 ring-primary')}
              onClick={() => {
                setStatusFilter('ALL');
                setTypeFilter((prev) => (prev === t ? 'ALL' : t));
              }}
            >
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', cfg.color)}>{cfg.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{count}</p>
                  <p className="text-xs text-muted-foreground">{cfg.label}</p>
                </div>
              </div>
            </Card>
          );
        })}
        {(() => {
          const inactiveCount = accounts.filter((a) => !a.is_active).length;
          const isSelected = statusFilter === 'INACTIVE';
          return (
            <Card
              className={cn('p-4 cursor-pointer transition-colors hover:bg-muted/40', isSelected && 'ring-2 ring-primary')}
              onClick={() => {
                setTypeFilter('ALL');
                if (isSelected) {
                  setStatusFilter('ALL');
                } else {
                  setStatusFilter('INACTIVE');
                  setShowInactive(true);
                }
              }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400"><Power className="w-4 h-4" /></div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{inactiveCount}</p>
                  <p className="text-xs text-muted-foreground">Inactivas</p>
                </div>
              </div>
            </Card>
          );
        })()}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">
              {search || typeFilter !== 'ALL' ? 'No hay resultados para este filtro.' : 'No hay cuentas registradas.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {filtered.length} cuenta{filtered.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <div className="divide-y divide-border">
            {pager.pageItems.map((account) => {
              const cfg = TYPE_CONFIG[account.type];
              const isBusy = busyId === account.id;
              const menuOpen = menuOpenFor === account.id;
              return (
                <div
                  key={account.id}
                  className="relative w-full flex items-center gap-4 px-6 py-4 hover:bg-muted/40 transition-colors text-left"
                >
                  <button
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    className="flex items-center gap-4 flex-1 min-w-0 text-left"
                  >
                    <div className={cn('p-2 rounded-lg shrink-0', cfg.color)}>{cfg.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground truncate">{account.name}</span>
                        <Badge variant="outline" className="text-xs font-mono shrink-0">
                          {account.account_number}
                        </Badge>
                        {account.client_tier && (
                          <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border shrink-0',
                            TIER_CONFIG[account.client_tier].bg,
                            TIER_CONFIG[account.client_tier].color,
                          )}>
                            <Award className="w-3 h-3" />
                            {account.client_tier}
                          </span>
                        )}
                        {!account.is_active && (
                          <Badge variant="secondary" className="text-xs">Inactiva</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {account.city && <span>{account.city}</span>}
                        {account.phone && <span>·  {account.phone}</span>}
                        {account.monthly_fee != null && (
                          <span>· Q{account.monthly_fee.toFixed(2)}/mes</span>
                        )}
                        <span>· {formatSeniority(account.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {account.is_active
                        ? <CheckCircle className="w-4 h-4 text-green-500" />
                        : <XCircle className="w-4 h-4 text-muted-foreground" />}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                  <PermissionGate require="accounts.manage">
                    <div className="relative shrink-0" ref={menuOpen ? menuRef : undefined}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenFor(menuOpen ? null : account.id);
                        }}
                        disabled={isBusy}
                        className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        aria-label="Acciones"
                      >
                        {isBusy
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <MoreVertical className="w-4 h-4" />}
                      </button>
                      {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-lg z-10 py-1">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(account)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-foreground text-left"
                          >
                            <Power className="w-4 h-4" />
                            {account.is_active ? 'Desactivar' : 'Reactivar'}
                          </button>
                          <div className="border-t border-border my-1" />
                          <p className="px-3 py-1 text-xs text-muted-foreground font-medium">Clasificación</p>
                          {(['A', 'B', 'C'] as AccountTier[]).map((tier) => (
                            <button
                              key={tier}
                              type="button"
                              onClick={() => handleSetTier(account, account.client_tier === tier ? null : tier)}
                              className={cn(
                                'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-left',
                                account.client_tier === tier ? TIER_CONFIG[tier].color + ' font-semibold' : 'text-foreground',
                              )}
                            >
                              <Award className="w-4 h-4" />
                              Tier {tier}
                              {account.client_tier === tier && <Check className="w-3 h-3 ml-auto" />}
                            </button>
                          ))}
                          <div className="border-t border-border my-1" />
                          <button
                            type="button"
                            onClick={() => handleDelete(account)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-500/10 text-red-600 dark:text-red-400 text-left"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </PermissionGate>
                </div>
              );
            })}
          </div>
          <Pagination
            className="border-t border-border px-6 py-3"
            page={pager.page}
            pageCount={pager.pageCount}
            pageSize={pager.pageSize}
            total={pager.total}
            onPageChange={pager.setPage}
            onPageSizeChange={pager.setPageSize}
            itemLabel="cuentas"
          />
        </Card>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4 my-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Nueva cuenta
            </h2>
            <form onSubmit={handleCreate} className="space-y-3">
              {/* Nombre + Tipo */}
              <div>
                <label className="text-sm font-medium text-foreground">Nombre *</label>
                <Input
                  required
                  placeholder="Ej: El Palacio de Las Telas"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Tipo *</label>
                <div className="flex gap-2 mt-1">
                  {(['residential', 'commercial', 'enterprise'] as AccountType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, type: t }))}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium border transition-colors',
                        form.type === t
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      )}
                    >
                      {TYPE_CONFIG[t].icon}
                      {TYPE_CONFIG[t].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* NIT + Nombre facturación */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground">NIT</label>
                  <Input
                    placeholder="CF o 1234567-8"
                    value={form.nit}
                    onChange={(e) => setForm((f) => ({ ...f, nit: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Teléfono</label>
                  <Input
                    placeholder="5566-7788"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Correo + Mensualidad */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground">Correo facturación</label>
                  <Input
                    type="email"
                    placeholder="factura@empresa.com"
                    value={form.billing_email}
                    onChange={(e) => setForm((f) => ({ ...f, billing_email: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Mensualidad (Q)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="350.00"
                    value={form.monthly_fee}
                    onChange={(e) => setForm((f) => ({ ...f, monthly_fee: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Departamento + Municipio */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground">Departamento</label>
                  <select
                    value={form.department_id}
                    onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">— Seleccionar —</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Municipio</label>
                  <select
                    value={form.municipality_id}
                    onChange={(e) => setForm((f) => ({ ...f, municipality_id: e.target.value }))}
                    disabled={!form.department_id}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                  >
                    <option value="">— Seleccionar —</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dirección */}
              <div>
                <label className="text-sm font-medium text-foreground">Dirección</label>
                <Input
                  placeholder="Ej: 5a Av 12-34, Zona 10"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  className="mt-1"
                />
              </div>

              {/* Notas */}
              <div>
                <label className="text-sm font-medium text-foreground">Notas</label>
                <Input
                  placeholder="Observaciones internas"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="mt-1"
                />
              </div>

              {error && (
                <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setError(null); }}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Crear cuenta
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
